<template>
  <div :class="classes()">
    <div v-for="(item, idx1) in content" :key="`seriveToggles-${idx1}`">
      <Headline :staticClass="idx1 > 0 ? `${el}__subline` : ''" size="xs">
        {{ item.headline }}
      </Headline>
      <div
        :class="{
          [`${el}__wrapper`]: true,
          [`${el}__wrapper--withBackground`]: idx1 === 0 && $matchMedia.l,
        }"
      >
        <div
          v-for="(row, idx2) in item.rows"
          :class="idx1 === 0 && idx2 === 0 && currentCareLevelIsCalculated ? `${el}__rowOuter` : ''"
          :key="`seriveToggles-${idx2}`"
        >
          <Copy
            v-if="idx1 === 0 && idx2 === 0 && !currentCareLevelIsCalculated"
            element="div"
            size="xl"
            bold
          >
            {{ $t(`cs.analysis.packageSelection.subHeading${idx2}`) }}
          </Copy>

          <div :class="`${el}__row`">
            <Label
              v-if="row.id === ourRecommendation"
              :label="$t('cs.analysis.budgetPackage.recommendation.toggle.tag')"
              size="m"
              bold
              color="supportSuccess"
              :class="`${el}__label`"
            />
            <div
              :class="{
                [`${el}__inner`]: true,
                [`${el}__inner--active`]:
                  careServiceFacts[row.id] === 1 || careServiceFacts[row.id] === 2,
                [`${el}__inner--noBottomBorderRadius`]:
                  (careServiceFacts[row.id] === 1 || careServiceFacts[row.id] === 2) &&
                  row.id === 'kombinationsleistung',
              }"
            >
              <div :class="`${el}__content`">
                <Copy element="div" color="tertiaryLight">{{ row.service }}</Copy>
                <Copy element="div" size="l" bold>{{ row.description }}</Copy>
                <Link
                  class="u-margin__top--2"
                  :onClick="() => openContentSheet(row.id)"
                  icon="arrow-right-line"
                  iconPos="right"
                >
                  {{ $t('cs.wizard.switcherToggle.link') }}
                </Link>
                <Copy
                  v-if="
                    row.id === 'kurzzeitpflege' &&
                    !careServiceFacts.verhinderungspflege &&
                    careServiceFacts.kurzzeitpflege
                  "
                  :staticClass="`${el}__notice`"
                  element="div"
                  size="s"
                >
                  <span
                    v-html="
                      $t('cs.wizard.kurzzeitpflege.infoText', {
                        value: formatNumber(getFact('verhinderungspflege').amount, 0),
                      })
                    "
                  />
                </Copy>
                <Copy
                  v-if="
                    row.id === 'verhinderungspflege' &&
                    !careServiceFacts.kurzzeitpflege &&
                    careServiceFacts.verhinderungspflege
                  "
                  :staticClass="`${el}__notice`"
                  element="div"
                  size="s"
                >
                  <span v-html="$t('cs.wizard.verhinderungspflege.infoText')" />
                </Copy>
              </div>
              <div :class="`${el}__price`">
                <template v-if="row.id === 'kombinationsleistung'">
                  {{
                    $t('cs.analysis.generals.monthly', {
                      value: formatNumber(
                        userInputFromSlider.pflegegeld.amount +
                          userInputFromSlider.pflegesachleistungen.amount,
                        0
                      ),
                    })
                  }}
                  <br />
                  {{
                    $t('cs.analysis.generals.yearly', {
                      value: formatNumber(
                        (userInputFromSlider.pflegegeld.amount +
                          userInputFromSlider.pflegesachleistungen.amount) *
                          12,
                        0
                      ),
                    })
                  }}
                </template>
                <template v-else>
                  <template v-if="payoutType(row.id) === 'monthly'">
                    {{
                      $t('cs.analysis.generals.monthly', {
                        value: formatNumber(getFact(row.id).amount),
                      })
                    }}
                    <br />
                    {{
                      $t('cs.analysis.generals.yearly', {
                        value: formatNumber(getFact(row.id).amount * 12, 0),
                      })
                    }}
                  </template>
                  <template v-else>
                    {{
                      $t(`cs.analysis.generals.${payoutType(row.id)}`, {
                        value: formatNumber(getFact(row.id).amount, 0),
                      })
                    }}
                  </template>
                </template>
              </div>
              <div :class="`${el}__select`">
                <Copy
                  element="div"
                  :staticClass="`${el}__approved`"
                  bold
                  v-if="careServiceFacts[row.id] === 2 || careServiceFacts[row.id] === 3"
                >
                  <div :class="`${el}__approvedIcon`">
                    <Icon name="check-fill" color="neutral0" />
                  </div>
                  {{ $t('cs.analysis.packageSelection.selectLabels')[1] }}
                </Copy>
                <template v-else>
                  <ToggleSwitch
                    v-if="careServiceCareLevel === careLevel"
                    :inactiveLabel="$t('cs.analysis.packageSelection.selectLabels')[3]"
                    :activeLabel="$t('cs.analysis.packageSelection.selectLabels')[2]"
                    labelOnSamePosition
                    :checked="careServiceFacts[row.id] === 1"
                    @click="onChange(row.id, careServiceFacts[row.id])"
                  />
                </template>
              </div>
            </div>
            <Slider
              :className="el"
              :show="
                (careServiceFacts[row.id] === 1 || careServiceFacts[row.id] === 2) &&
                row.id === 'kombinationsleistung'
              "
              @trackSliderMovement="
                onTrackingClick('Finanzielle-Leistungen_interaction-Schieberegler', 'element')
              "
            />
          </div>
        </div>
      </div>
    </div>
    <Modal
      :open="showModal"
      @close="showModal = false"
      size="s"
      :headline="$t('cs.analysis.packageSelection.modal.headline')"
      @onTrackingClick="trackCloseModal"
    >
      <Copy
        :staticClass="`${el}__modalCopy`"
        element="div"
        size="l"
        color="primaryBlue"
        bold
        centered
      >
        {{ modalCopy }}
      </Copy>
      <div :class="`${el}__modalButtons`">
        <Button
          :label="$t('cs.analysis.packageSelection.modal.yes')"
          type="outlined"
          small
          :loading="loadingModal"
          @click="onUpdateBasics"
        />
        <Button
          :label="$t('cs.analysis.packageSelection.modal.no')"
          type="outlined"
          @click="showModal = false"
          small
        />
      </div>
    </Modal>
  </div>
</template>

<script>
import Headline from '@/objects/Headline';
import Copy from '@/objects/Copy';
import Link from '@/objects/Link';
import Button from '@/objects/Button';
import Icon from '@/objects/Icon';
import Label from '@/objects/Label';
import ToggleSwitch from '@/objects/ToggleSwitch';
import Modal from '@/components/Modal';

import careServiceWizard from '@/content/services/CareService/CareService';

import { CONTENT_SHEET_CONTENT } from '@/store/actions/contentSheet';
import { CARESERVICE_UPDATE_FACT } from '@/store/actions/careService';

import mixin from '@/mixins/careService';

import Slider from './subcomponents/Slider';

export default {
  name: 'CareServiceBudgetSelection',
  props: {},
  mixins: [mixin],
  components: {
    Headline,
    Copy,
    Link,
    Button,
    Icon,
    Label,
    ToggleSwitch,
    Slider,
    Modal,
  },
  data() {
    return {
      showBar: false,
      showModal: false,
      loadingModal: false,
      modalCopy: '',
      updateBasics: [],
      el: 'c-careServiceBudgetSelection',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },

  computed: {
    content() {
      const { toggles, togglesPg1 } = careServiceWizard.filter(
        (c) => c.component.name === 'ServiceToggles'
      )[0].component.props;
      return this.careServiceCareLevel === 1 ? togglesPg1 : toggles;
    },
  },

  methods: {
    openContentSheet(sheetId) {
      this.$store.dispatch(CONTENT_SHEET_CONTENT, { sheetId: `pflegeleistungen/${sheetId}` });
    },

    onUpdateBasics() {
      const close = () => {
        this.showModal = false;
        this.loadingModal = false;
      };

      this.loadingModal = true;

      this.$store
        .dispatch(CARESERVICE_UPDATE_FACT, this.updateBasics[0])
        .then(() => {
          this.$store
            .dispatch(CARESERVICE_UPDATE_FACT, this.updateBasics[1])
            .then(close)
            .catch(close);
        })
        .catch(close);
    },

    updateModalContent(copy) {
      this.modalCopy = copy + ' Wollen Sie die neue Leistung erhalten?';
      this.showModal = true;
    },

    resetId(id) {
      this.$store.dispatch(CARESERVICE_UPDATE_FACT, {
        id,
        value: 2,
      });
    },

    onChange(id, currentValue) {
      this.updateBasics = [];
      const nextValue = currentValue === 1 ? 0 : 1;

      /**
       * Es gibt Sonderfälle für Pflegegeld, Pflegesachleistungen und Kombinationsleistung
       *
       */
      if (id === 'pflegegeld') {
        if (nextValue === 1) {
          if (this.careServiceFacts.pflegesachleistungen === 1) {
            this.updateModalContent(
              'Sie wünschen sich bereits die Pflegesachleistungen. Wenn sie zusätzlich Pflegegeld wünschen, bräuchten Sie die Kombinationsleistung.'
            );
            if (this.careServiceFacts.kombinationsleistung > 1) {
              this.updateBasics = [
                { id: 'pflegesachleistungen', value: 0 },
                { id: 'kombinationsleistung', value: 2 },
              ];
            } else {
              this.updateBasics = [
                { id: 'pflegesachleistungen', value: 0 },
                { id: 'kombinationsleistung', value: 1 },
              ];
            }
          } else if (this.careServiceFacts.pflegesachleistungen === 2) {
            this.updateModalContent(
              'Das Pflegegeld ersetzt Ihre bisher genehmigte Leistung (Pflegesachleistungen).'
            );
            this.updateBasics = [
              { id: 'pflegesachleistungen', value: 3 },
              { id: 'pflegegeld', value: 1 },
            ];
          } else if (this.careServiceFacts.kombinationsleistung === 1) {
            this.updateModalContent(
              'Das Pflegegeld ersetzt Ihre bisherige Wunschleistung (Kombinationsleistung).'
            );
            this.updateBasics = [
              { id: 'pflegegeld', value: 1 },
              { id: 'kombinationsleistung', value: 0 },
            ];
          } else if (this.careServiceFacts.kombinationsleistung === 2) {
            this.updateModalContent(
              'Das Pflegegeld ersetzt Ihre bisher genehmigte Leistung (Kombinationsleistung).'
            );
            this.updateBasics = [
              { id: 'pflegegeld', value: 1 },
              { id: 'kombinationsleistung', value: 3 },
            ];
          } else {
            this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
          }
        } else {
          if (this.careServiceFacts.kombinationsleistung === 3) {
            this.resetId('kombinationsleistung');
          } else if (this.careServiceFacts.pflegesachleistungen === 3) {
            this.resetId('pflegesachleistungen');
          }
          this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
        }
      } else if (id === 'pflegesachleistungen') {
        if (nextValue === 1) {
          if (this.careServiceFacts.pflegegeld === 1) {
            this.updateModalContent(
              'Sie wünschen sich bereits das Pflegegeld. Wenn sie zusätzlich Pflegesachleistung wünschen, bräuchten Sie die Kombinationsleistung.'
            );
            if (this.careServiceFacts.kombinationsleistung > 1) {
              this.updateBasics = [
                { id: 'pflegegeld', value: 0 },
                { id: 'kombinationsleistung', value: 2 },
              ];
            } else {
              this.updateBasics = [
                { id: 'pflegegeld', value: 0 },
                { id: 'kombinationsleistung', value: 1 },
              ];
            }
          } else if (this.careServiceFacts.pflegegeld === 2) {
            this.updateModalContent(
              'Die Pflegesachleistung ersetzt Ihre bisher genehmigte Leistung (Pflegegeld).'
            );
            this.updateBasics = [
              { id: 'pflegegeld', value: 3 },
              { id: 'pflegesachleistungen', value: 1 },
            ];
          } else if (this.careServiceFacts.kombinationsleistung === 1) {
            this.updateModalContent(
              'Die Pflegesachleistung ersetzt Ihre bisherige Wunschleistung (Kombinationsleistung).'
            );
            this.updateBasics = [
              { id: 'pflegesachleistungen', value: 1 },
              { id: 'kombinationsleistung', value: 0 },
            ];
          } else if (this.careServiceFacts.kombinationsleistung === 2) {
            this.updateModalContent(
              'Die Pflegesachleistung ersetzt Ihre bisher genehmigte Leistung (Kombinationsleistung).'
            );
            this.updateBasics = [
              { id: 'pflegesachleistungen', value: 1 },
              { id: 'kombinationsleistung', value: 3 },
            ];
          } else {
            this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
          }
        } else {
          if (this.careServiceFacts.pflegegeld === 3) {
            this.resetId('pflegegeld');
          } else if (this.careServiceFacts.kombinationsleistung === 3) {
            this.resetId('kombinationsleistung');
          }
          this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
        }
      } else if (id === 'kombinationsleistung') {
        if (nextValue === 1) {
          if (this.careServiceFacts.pflegegeld === 1) {
            this.updateModalContent(
              'Die Kombinationsleistung ersetzt Ihre bisherige Wunschleistung (Pflegegeld).'
            );
            this.updateBasics = [
              { id: 'kombinationsleistung', value: 1 },
              { id: 'pflegegeld', value: 0 },
            ];
          } else if (this.careServiceFacts.pflegegeld === 2) {
            this.updateModalContent(
              'Die Kombinationsleistung ersetzt Ihre bisher genehmigte Leistung (Pflegegeld).'
            );
            this.updateBasics = [
              { id: 'pflegegeld', value: 3 },
              { id: 'kombinationsleistung', value: 1 },
            ];
          } else if (this.careServiceFacts.pflegesachleistungen === 1) {
            this.updateModalContent(
              'Die Kombinationsleistung ersetzt Ihre bisherige Wunschleistung (Pflegesachleistung).'
            );
            this.updateBasics = [
              { id: 'kombinationsleistung', value: 1 },
              { id: 'pflegesachleistungen', value: 0 },
            ];
          } else if (this.careServiceFacts.pflegesachleistungen === 2) {
            this.updateModalContent(
              'Die Kombinationsleistung ersetzt Ihre bisher genehmigte Leistung (Pflegesachleistung).'
            );
            this.updateBasics = [
              { id: 'pflegesachleistungen', value: 3 },
              { id: 'kombinationsleistung', value: 1 },
            ];
          } else {
            this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
          }
        } else {
          if (this.careServiceFacts.pflegegeld === 3) {
            this.resetId('pflegegeld');
          } else if (this.careServiceFacts.pflegesachleistungen === 3) {
            this.resetId('pflegesachleistungen');
          }
          this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
        }
      } else {
        this.$store.dispatch(CARESERVICE_UPDATE_FACT, { id, value: nextValue });
      }
    },

    trackCloseModal(source) {
      let label = this.$t('cs.analysis.packageSelection.modal.headline') + '-Pop-up';
      switch (source) {
        case 'icon':
          label = label + '_click-X';
          break;
        case 'background':
          label = label + '_click-background';
          break;
      }
      this.onTrackingClick(label, 'element');
    },

    onTrackingClick(label, element) {
      this.$emit('onTrackingClick', label, element);
    },
  },
};
</script>
